import React, { useCallback, useEffect, useState } from 'react';
import { FileWarning, Check, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../store/useStore';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import '../styles/ConsensiDaReinformare.css';

/**
 * ConsensiDaReinformare — chi sta proseguendo un ciclo su un consenso
 * che nel frattempo è cambiato.
 *
 * PERCHE' ESISTE
 * Un ciclo di sedute dura mesi. Il paziente firma il consenso all'inizio
 * e il ciclo prosegue su QUELLA versione: non gli si chiede di rifirmare
 * a metà ciclo. Se nel frattempo il modello viene rivisto e la revisione
 * aggiunge un rischio che prima non era scritto, quel paziente deve
 * saperlo — e glielo dice il medico alla seduta successiva.
 *
 * Senza questa schermata la domanda «chi ha un ciclo aperto su una
 * versione vecchia» si risponde aprendo i PDF uno per uno, cioè non si
 * risponde.
 *
 * COSA NON FA
 * Non manda niente a nessuno e non chiude niente da sola. Serve a
 * sapere, e a lasciare scritto che si è parlato: la conversazione col
 * paziente resta una cosa fra persone.
 */

const dataIta = (v) => {
    if (!v) return null;
    const d = new Date(String(v).slice(0, 10));
    return isNaN(d) ? null : d.toLocaleDateString('it-IT');
};

const ConsensiDaReinformare = () => {
    const user = useAuthStore((s) => s.user);
    const puoAnnotare = ['medico', 'medico_senior', 'admin'].includes(user?.ruolo);

    const [modelli, setModelli] = useState([]);
    const [cicli, setCicli] = useState([]);
    const [modelloScelto, setModelloScelto] = useState(null);
    const [caricando, setCaricando] = useState(true);
    const [errore, setErrore] = useState(null);

    const [aperto, setAperto] = useState(null);     // pacchetto in cui si sta scrivendo
    const [nota, setNota] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [esito, setEsito] = useState(null);

    const carica = useCallback(async () => {
        setCaricando(true);
        try {
            const [m, c] = await Promise.all([
                api.get('/versioni-consenso/modelli'),
                api.get('/versioni-consenso/cicli', {
                    params: modelloScelto ? { template_id: modelloScelto } : {},
                }),
            ]);
            setModelli(m.data?.data?.modelli || []);
            setCicli(c.data?.data?.cicli || []);
            setErrore(null);
        } catch (e) {
            setErrore('Non riesco a leggere l’elenco');
        } finally {
            setCaricando(false);
        }
    }, [modelloScelto]);

    useEffect(() => { carica(); }, [carica]);

    const annota = async (pacchettoId) => {
        if (!nota.trim()) return;
        setSalvando(true);
        try {
            const r = await api.post('/versioni-consenso/reinformazioni', {
                pacchetto_id: pacchettoId,
                nota: nota.trim(),
            });
            setEsito({ ok: true, testo: r.data?.message });
            setAperto(null);
            setNota('');
            carica();
        } catch (e) {
            setEsito({ ok: false, testo: e.response?.data?.message || 'Non riesco a salvare' });
        } finally {
            setSalvando(false);
        }
    };

    const daFare = cicli.filter((c) => !c.reinformato_il).length;

    return (
        <div className="cdr-pagina">
            <IntestazioneSezione
                titolo="Consensi cambiati a ciclo aperto"
                icona={<FileWarning size={22} aria-hidden="true" />}
                sottotitolo={<>
                    Pazienti che stanno proseguendo un ciclo di sedute sulla versione
                    del consenso che hanno firmato all’inizio, mentre il modello è
                    stato rivisto nel frattempo. <b>Non gli si chiede di rifirmare</b>:
                    se la revisione ha aggiunto un rischio che prima non era scritto,
                    è il medico a dirglielo alla seduta successiva, e ad annotarlo qui.
                </>}
            />

            {errore && <p className="cdr-errore">{errore}</p>}
            {esito && (
                <p className={esito.ok ? 'cdr-esito-ok' : 'cdr-errore'}>{esito.testo}</p>
            )}

            {modelli.length > 1 && (
                <div className="cdr-modelli">
                    <button className={!modelloScelto ? 'attivo' : ''}
                            onClick={() => setModelloScelto(null)}>
                        Tutti
                    </button>
                    {modelli.map((m) => (
                        <button key={m.template_id}
                                className={modelloScelto === m.template_id ? 'attivo' : ''}
                                onClick={() => setModelloScelto(m.template_id)}>
                            {m.template_titolo}
                            <span className="cdr-pallino">{m.da_informare}</span>
                        </button>
                    ))}
                </div>
            )}

            {caricando ? <p className="cdr-attesa">Carico…</p> : cicli.length === 0 ? (
                <div className="cdr-vuoto">
                    <Check size={30} strokeWidth={1.4} aria-hidden="true" />
                    <p><b>Nessun ciclo su una versione precedente.</b></p>
                    <p>
                        Tutti i cicli aperti corrono sulla versione corrente del
                        loro consenso. Questa pagina si popola da sola quando un
                        modello viene modificato nel testo.
                    </p>
                </div>
            ) : (
                <>
                    <p className="cdr-conteggio">
                        <b>{daFare}</b> {daFare === 1 ? 'paziente' : 'pazienti'} da informare
                        {cicli.length - daFare > 0 && `, ${cicli.length - daFare} già informati`}
                    </p>

                    <ul className="cdr-elenco">
                        {cicli.map((c) => (
                            <li key={c.pacchetto_id}
                                className={`cdr-riga ${c.reinformato_il ? 'fatto' : 'da-fare'}`}>
                                <div className="cdr-riga-testa">
                                    <div>
                                        <span className="cdr-paziente">{c.cognome} {c.nome}</span>
                                        <span className="cdr-ciclo">{c.descrizione}</span>
                                    </div>
                                    <div className="cdr-versioni">
                                        <span className="cdr-firmata">ha firmato la v{c.versione_firmata}</span>
                                        <span className="cdr-freccia">→</span>
                                        <span className="cdr-attuale">
                                            oggi è la v{c.versione_attuale}
                                            {c.versione_data && ` del ${dataIta(c.versione_data)}`}
                                        </span>
                                    </div>
                                </div>

                                <div className="cdr-riga-dati">
                                    <span><b>{c.sedute_eseguite}</b> sedute fatte, <b>{c.sedute_residue}</b> ancora da fare</span>
                                    <span className="cdr-modello">{c.template_titolo}</span>
                                    {c.telefono && <span className="cdr-telefono">{c.telefono}</span>}
                                </div>

                                {c.reinformato_il ? (
                                    <p className="cdr-fatto-riga">
                                        <Check size={14} aria-hidden="true" />
                                        Informato il {dataIta(c.reinformato_il)}
                                    </p>
                                ) : puoAnnotare ? (
                                    <div className="cdr-annota">
                                        <button className="cdr-apri"
                                                onClick={() => {
                                                    setAperto(aperto === c.pacchetto_id ? null : c.pacchetto_id);
                                                    setNota('');
                                                }}>
                                            {aperto === c.pacchetto_id
                                                ? <>Chiudi <ChevronUp size={14} /></>
                                                : <>Ho informato il paziente <ChevronDown size={14} /></>}
                                        </button>

                                        {aperto === c.pacchetto_id && (
                                            <div className="cdr-modulo">
                                                <label htmlFor={`nota-${c.pacchetto_id}`}>
                                                    Cosa hai spiegato al paziente
                                                </label>
                                                <textarea id={`nota-${c.pacchetto_id}`}
                                                          rows={3}
                                                          value={nota}
                                                          onChange={(e) => setNota(e.target.value)}
                                                          placeholder="Es.: spiegato il rischio aggiunto nella versione 3 prima della quarta seduta; paziente d'accordo a proseguire." />
                                                <p className="cdr-perche">
                                                    La nota è obbligatoria: fra due anni «informato» da solo
                                                    non dice di che cosa.
                                                </p>
                                                <button className="cdr-salva"
                                                        disabled={!nota.trim() || salvando}
                                                        onClick={() => annota(c.pacchetto_id)}>
                                                    {salvando ? 'Salvo…' : 'Annota'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <p className="cdr-solo-medico">
                                        L’annotazione la scrive il medico che ha parlato col paziente.
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </div>
    );
};

export default ConsensiDaReinformare;
